<template>
  <div class="min-h-screen bg-gray-50">
    <!-- Header -->
    <header class="bg-white shadow-sm border-b">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex justify-between items-center py-4">
          <div>
            <h1 class="text-2xl font-bold text-gray-900">All Clients</h1>
            <p class="text-gray-600">Super Admin Dashboard</p>
          </div>
          <button @click="navigateTo('/admin/dashboard')" class="btn-tertiary">
            Back to Dashboard
          </button>
        </div>
      </div>
    </header>

    <!-- Main Content -->
    <main class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <!-- Loading State -->
      <div v-if="loading" class="flex justify-center items-center py-12">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="bg-red-50 border border-red-200 rounded-lg p-6">
        <div class="flex">
          <div class="flex-shrink-0">
            <svg class="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"></path>
            </svg>
          </div>
          <div class="ml-3">
            <h3 class="text-sm font-medium text-red-800">Error loading clients</h3>
            <p class="mt-1 text-sm text-red-700">{{ error }}</p>
          </div>
        </div>
      </div>

      <!-- Clients Table -->
      <div v-else class="bg-white rounded-lg shadow overflow-hidden">
        <div class="px-6 py-4 border-b border-gray-200">
          <h2 class="text-lg font-medium text-gray-900">All Platform Clients</h2>
          <p class="mt-1 text-sm text-gray-600">{{ clients.length }} total clients</p>
        </div>
        
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Company
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Email
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Shows
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Leads
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Subscription
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Access
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Created
              </th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <template v-for="client in clients" :key="client.id">
              <tr class="hover:bg-gray-50">
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="text-sm font-medium text-gray-900">{{ client.company_name }}</div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="text-sm text-gray-900">{{ client.email }}</div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="text-sm text-gray-900">{{ client.total_shows }}</div>
                  <button 
                    v-if="client.total_shows > 0"
                    @click="toggleClientShows(client.id)"
                    class="text-xs text-blue-600 hover:text-blue-800 mt-1"
                  >
                    {{ expandedClients.includes(client.id) ? 'Hide Shows' : 'View Shows' }}
                  </button>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="text-sm text-gray-900">{{ client.total_leads }}</div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="space-y-1">
                    <span class="inline-flex items-center rounded-full px-2.5 py-1 text-[12px] font-medium"
                          :class="getStatusClass(client.subscription_status)">
                      {{ client.subscription_status }}
                    </span>
                    <div class="text-xs text-gray-600">
                      {{ client.subscription_type }}
                      <span v-if="client.days_remaining !== null" class="ml-1">
                        ({{ client.days_remaining }} days left)
                      </span>
                    </div>
                    <div v-if="client.subscription_expires_at" class="text-xs text-gray-500">
                      Expires: {{ formatDate(client.subscription_expires_at) }}
                    </div>
                  </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <StatusBadge 
                    :status="client.can_access_admin ? 'active' : 'blocked'"
                    :custom-text="client.can_access_admin ? 'Active' : 'Blocked'"
                  />
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm">
                  <button 
                    @click="viewSubscriptionDetails(client.id)"
                    class="text-gray-600 hover:text-gray-800"
                  >
                    Details →
                  </button>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {{ formatDate(client.created_at) }}
                </td>
              </tr>
              
              <!-- Expandable Shows Section -->
              <tr v-if="expandedClients.includes(client.id)">
                <td colspan="9" class="px-6 py-4 bg-gray-50">
                  <div class="space-y-4">
                    <div class="flex justify-between items-center">
                      <h4 class="text-lg font-medium text-gray-900">{{ client.company_name }} - Shows</h4>
                      <button 
                        @click="refreshClientShows(client.id)"
                        :disabled="refreshingShows === client.id"
                        class="text-sm text-blue-600 hover:text-blue-800"
                      >
                        <span v-if="refreshingShows === client.id" class="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-1"></span>
                        Refresh
                      </button>
                    </div>
                    
                    <!-- Loading Shows -->
                    <div v-if="loadingShows === client.id" class="flex justify-center py-4">
                      <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                      <p class="ml-2 text-gray-600">Loading shows...</p>
                    </div>
                    
                    <!-- Shows Grid -->
                    <div v-else-if="clientShows[client.id] && clientShows[client.id].length > 0" class="grid gap-4">
                      <div 
                        v-for="show in clientShows[client.id]" 
                        :key="show.id"
                        class="bg-white rounded-lg border p-4 shadow-sm"
                      >
                        <div class="flex justify-between items-start mb-3">
                          <div>
                            <h5 class="text-lg font-medium text-gray-900">{{ show.name }}</h5>
                            <p class="text-sm text-gray-600">{{ show.start_date }} - {{ show.end_date }}</p>
                          </div>
                          <div class="flex space-x-2">
                            <StatusBadge :status="show.status" />
                            <StatusBadge 
                              v-if="show.payment_status"
                              :status="show.payment_status === 'paid' ? 'paid' : 'payment_pending'"
                              :custom-text="show.payment_status === 'paid' ? 'Paid' : 'Payment Pending'"
                            />
                          </div>
                        </div>
                        
                        <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                          <div>
                            <p class="text-sm text-gray-600">
                              <span class="font-medium">Tablets:</span> {{ show.tablet_count }}
                            </p>
                            <p class="text-sm text-gray-600">
                              <span class="font-medium">Leads:</span> {{ show.lead_count }}
                            </p>
                            <p v-if="show.final_price" class="text-sm text-gray-600">
                              <span class="font-medium">Price:</span> €{{ show.final_price }}
                            </p>
                          </div>
                          <div>
                            <p class="text-sm text-gray-600">
                              <span class="font-medium">Public URL:</span>
                            </p>
                            <div class="flex items-center space-x-2 mt-1">
                              <input 
                                :value="getPublicUrl(client.slug, show.slug)"
                                readonly
                                class="text-xs bg-gray-100 px-2 py-1 rounded border flex-1"
                              />
                              <button 
                                @click="copyToClipboard(getPublicUrl(client.slug, show.slug))"
                                class="text-xs text-blue-600 hover:text-blue-800"
                              >
                                Copy
                              </button>
                            </div>
                          </div>
                          <div>
                            <p class="text-sm text-gray-600">
                              <span class="font-medium">Created:</span> {{ formatDate(show.created_at) }}
                            </p>
                            <p v-if="show.notes" class="text-sm text-gray-600">
                              <span class="font-medium">Notes:</span> {{ show.notes }}
                            </p>
                            <!-- Recently Modified Indicator -->
                            <p v-if="show.recently_modified" class="text-sm text-orange-600 font-medium">
                              <span class="inline-flex items-center">
                                <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                </svg>
                                Recently Modified
                              </span>
                            </p>
                          </div>
                        </div>
                        
                        <!-- Show Management Actions -->
                        <div class="border-t pt-4">
                          <div class="flex flex-wrap gap-2">
                            <button 
                              v-if="canClientEditShow(show)"
                              @click="editShow(show)"
                              class="btn-secondary text-sm"
                            >
                              Edit Show
                            </button>
                            
                            <!-- Status-specific buttons -->
                            <button 
                              v-if="show.status === 'draft'"
                              @click="requestQuote(show)"
                              :disabled="updatingShow === show.id"
                              class="btn-primary text-sm"
                            >
                              <span v-if="updatingShow === show.id" class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-1"></span>
                              Request Quote
                            </button>
                            
                            <button 
                              v-if="show.status === 'approved' && show.payment_status === 'pending'"
                              @click="markShowPaid(show)"
                              :disabled="updatingShow === show.id"
                              class="btn-primary text-sm"
                            >
                              <span v-if="updatingShow === show.id" class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-1"></span>
                              Mark as Paid
                            </button>
                            
                            <button 
                              v-if="show.status === 'active'"
                              @click="toggleShowStatus(show, 'closed')"
                              :disabled="updatingShow === show.id"
                              class="btn-secondary text-sm"
                            >
                              <span v-if="updatingShow === show.id" class="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-1"></span>
                              Close Show
                            </button>
                            
                            <button 
                              v-if="show.status === 'closed'"
                              @click="toggleShowStatus(show, 'active')"
                              :disabled="updatingShow === show.id"
                              class="btn-primary text-sm"
                            >
                              <span v-if="updatingShow === show.id" class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-1"></span>
                              Reactivate
                            </button>
                            
                            <button 
                              v-if="show.status === 'closed'"
                              @click="toggleShowStatus(show, 'archived')"
                              :disabled="updatingShow === show.id"
                              class="btn-secondary text-sm"
                            >
                              <span v-if="updatingShow === show.id" class="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-1"></span>
                              Archive
                            </button>
                            
                            <button 
                              @click="viewShowLeads(show)"
                              class="btn-secondary text-sm"
                            >
                              View Leads ({{ show.lead_count }})
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <!-- No Shows -->
                    <div v-else class="text-center py-8 text-gray-500">
                      <p>No shows found for this client.</p>
                    </div>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>

        <!-- Empty State -->
        <div v-if="clients.length === 0" class="text-center py-12">
          <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
          </svg>
          <h3 class="mt-2 text-sm font-medium text-gray-900">No clients found</h3>
          <p class="mt-1 text-sm text-gray-500">No clients have been created yet.</p>
        </div>
      </div>
    </main>

    <!-- Subscription Details Modal -->
    <div v-if="showSubscriptionModal" class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div class="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div class="mt-3">
          <div class="flex justify-between items-center mb-4">
            <h3 class="text-lg font-medium text-gray-900">Subscription Details</h3>
            <button @click="showSubscriptionModal = false" class="text-gray-400 hover:text-gray-600">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
          
          <div v-if="subscriptionDetails" class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700">Company</label>
              <p class="mt-1 text-sm text-gray-900">{{ subscriptionDetails.company_name }}</p>
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700">Email</label>
              <p class="mt-1 text-sm text-gray-900">{{ subscriptionDetails.email }}</p>
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700">Subscription Status</label>
              <span class="mt-1 px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
                    :class="getStatusClass(subscriptionDetails.subscription_status)">
                {{ subscriptionDetails.subscription_status }}
              </span>
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700">Subscription Type</label>
              <p class="mt-1 text-sm text-gray-900">{{ subscriptionDetails.subscription_type }}</p>
            </div>
            
            <div v-if="subscriptionDetails.subscription_expires_at">
              <label class="block text-sm font-medium text-gray-700">Expires At</label>
              <p class="mt-1 text-sm text-gray-900">{{ formatDate(subscriptionDetails.subscription_expires_at) }}</p>
            </div>
            
            <div v-if="subscriptionDetails.days_remaining !== null">
              <label class="block text-sm font-medium text-gray-700">Days Remaining</label>
              <p class="mt-1 text-sm text-gray-900">{{ subscriptionDetails.days_remaining }} days</p>
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700">Can Access Admin</label>
              <StatusBadge 
                :status="subscriptionDetails.can_access_admin ? 'active' : 'blocked'"
                :custom-text="subscriptionDetails.can_access_admin ? 'Yes' : 'No'"
              />
            </div>
            
            <div v-if="subscriptionDetails.last_payment_date">
              <label class="block text-sm font-medium text-gray-700">Last Payment</label>
              <p class="mt-1 text-sm text-gray-900">{{ formatDate(subscriptionDetails.last_payment_date) }}</p>
            </div>
            
            <div v-if="subscriptionDetails.next_payment_date">
              <label class="block text-sm font-medium text-gray-700">Next Payment</label>
              <p class="mt-1 text-sm text-gray-900">{{ formatDate(subscriptionDetails.next_payment_date) }}</p>
            </div>
          </div>
          
          <div class="flex justify-end space-x-3 mt-6">
            <button @click="showSubscriptionModal = false" class="btn-secondary">
              Close
            </button>
            <button 
              v-if="subscriptionDetails && !subscriptionDetails.can_access_admin"
              @click="extendSubscription(selectedClient.id)"
              class="btn-primary"
            >
              Extend Subscription
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Extend Subscription Modal -->
    <div v-if="showExtendModal" class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div class="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div class="mt-3">
          <div class="flex justify-between items-center mb-4">
            <h3 class="text-lg font-medium text-gray-900">Extend Subscription</h3>
            <button @click="showExtendModal = false" class="text-gray-400 hover:text-gray-600">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
          
          <div v-if="selectedClient" class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700">Company</label>
              <p class="mt-1 text-sm text-gray-900">{{ selectedClient.company_name }}</p>
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700">Email</label>
              <p class="mt-1 text-sm text-gray-900">{{ selectedClient.email }}</p>
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700">Current Expiration</label>
              <p class="mt-1 text-sm text-gray-900">
                {{ selectedClient.subscription_expires_at ? formatDate(selectedClient.subscription_expires_at) : 'No expiration date' }}
              </p>
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700">Extend by (days)</label>
              <input 
                v-model="extendDays" 
                type="number" 
                min="1" 
                max="365"
                class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              >
            </div>
          </div>
          
          <div class="flex justify-end space-x-3 mt-6">
            <button @click="showExtendModal = false" class="btn-secondary">
              Cancel
            </button>
            <button 
              @click="confirmExtendSubscription"
              :disabled="loadingExtend"
              class="btn-primary"
            >
              <span v-if="loadingExtend" class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-1"></span>
              Extend Subscription
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed } from 'vue'
import { formatDate as fmtDate } from '../utils/date.js'
import { useRouter } from 'vue-router'
import { useAuthStore } from '../stores/authStore.js'
import apiClient from '../utils/api.js'
import StatusBadge from '../components/StatusBadge.vue'
import config from '../config.js'

// Router
const router = useRouter()

// Reactive data
const loading = ref(true)
const error = ref(null)
const clients = ref([])
const expandedClients = ref([])
const clientShows = ref({})
const loadingShows = ref(null)
const refreshingShows = ref(null)
const updatingShow = ref(null)

// Subscription management
const showSubscriptionModal = ref(false)
const showExtendModal = ref(false)
const selectedClient = ref(null)
const subscriptionDetails = ref(null)
const extendDays = ref(30)
const loadingSubscription = ref(false)
const loadingExtend = ref(false)

// Methods
const loadClients = async () => {
  try {
    loading.value = true
    error.value = null
    
    const token = localStorage.getItem('admin_token')
    if (!token) {
      router.push('/admin/login')
      return
    }

    const response = await fetch(`${config.apiBaseUrl}/admin/super-admin/clients`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })

    if (!response.ok) {
      if (response.status === 401) {
        localStorage.removeItem('admin_token')
        router.push('/admin/login')
        return
      }
      if (response.status === 403) {
        error.value = 'Super admin access required'
        return
      }
      throw new Error('Failed to load clients')
    }

    const data = await response.json()
    clients.value = data.clients
    
  } catch (err) {
    console.error('Error loading clients:', err)
    error.value = err.message || 'Failed to load clients'
  } finally {
    loading.value = false
  }
}

const toggleClientShows = async (clientId) => {
  if (expandedClients.value.includes(clientId)) {
    expandedClients.value = expandedClients.value.filter(id => id !== clientId)
  } else {
    expandedClients.value.push(clientId)
    if (!clientShows.value[clientId]) {
      await loadClientShows(clientId)
    }
  }
}

const loadClientShows = async (clientId) => {
  try {
    loadingShows.value = clientId
    
    const token = localStorage.getItem('admin_token')
    if (!token) {
      router.push('/admin/login')
      return
    }

    const response = await fetch(`${config.apiBaseUrl}/admin/super-admin/clients/${clientId}/shows`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })

    if (!response.ok) {
      throw new Error(`Failed to load client shows: ${response.status}`)
    }

    const data = await response.json()
    clientShows.value[clientId] = data.shows
    
  } catch (err) {
    console.error('Error loading client shows:', err)
    alert(`Error loading shows: ${err.message}`)
  } finally {
    loadingShows.value = null
  }
}

const refreshClientShows = async (clientId) => {
  try {
    refreshingShows.value = clientId
    await loadClientShows(clientId)
  } finally {
    refreshingShows.value = null
  }
}

const markShowPaid = async (show) => {
  try {
    updatingShow.value = show.id
    
    const token = localStorage.getItem('admin_token')
    if (!token) {
      router.push('/admin/login')
      return
    }

    const response = await fetch(`${config.apiBaseUrl}/admin/super-admin/shows/${show.id}/mark-paid`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.error || 'Failed to mark as paid')
    }

    // Update the show locally
    const clientId = Object.keys(clientShows.value).find(id => 
      clientShows.value[id].some(s => s.id === show.id)
    )
    if (clientId) {
      const showIndex = clientShows.value[clientId].findIndex(s => s.id === show.id)
      if (showIndex !== -1) {
        clientShows.value[clientId][showIndex].payment_status = 'paid'
      }
    }
    
    alert('Show marked as paid successfully!')
    
  } catch (err) {
    console.error('Error marking show as paid:', err)
    alert(`Error: ${err.message}`)
  } finally {
    updatingShow.value = null
  }
}

const toggleShowStatus = async (show, newStatus) => {
  try {
    updatingShow.value = show.id
    
    const token = localStorage.getItem('admin_token')
    if (!token) {
      router.push('/admin/login')
      return
    }

    const response = await fetch(`${config.apiBaseUrl}/admin/super-admin/shows/${show.id}/status`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ status: newStatus })
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.error || 'Failed to update show status')
    }

    // Update the show locally
    const clientId = Object.keys(clientShows.value).find(id => 
      clientShows.value[id].some(s => s.id === show.id)
    )
    if (clientId) {
      const showIndex = clientShows.value[clientId].findIndex(s => s.id === show.id)
      if (showIndex !== -1) {
        clientShows.value[clientId][showIndex].status = newStatus
      }
    }
    
    alert(`Show ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully!`)
    
  } catch (err) {
    console.error('Error updating show status:', err)
    alert(`Error: ${err.message}`)
  } finally {
    updatingShow.value = null
  }
}

const editShow = (show) => {
  // For now, we'll show an alert with show details
  // In the future, this could open a modal or navigate to an edit page
  const showInfo = `
Show: ${show.name}
Status: ${show.status}
Tablets: ${show.tablet_count || 'Not set'}
Start Date: ${show.start_date}
End Date: ${show.end_date}
Payment Status: ${show.payment_status}
Price: ${show.final_price ? `€${show.final_price}` : 'Not set'}
Notes: ${show.notes || 'None'}
  `.trim()
  
  alert(`Edit Show Details:\n\n${showInfo}\n\nNote: Full editing functionality will be implemented in a future update.`)
}

const requestQuote = async (show) => {
  try {
    updatingShow.value = show.id
    
    const token = localStorage.getItem('admin_token')
    if (!token) {
      router.push('/admin/login')
      return
    }

    const response = await fetch(`${config.apiBaseUrl}/admin/shows/${show.id}/request-quote`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ notes: 'Quote requested by super admin' })
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.error || 'Failed to request quote')
    }

    // Update the show locally
    const clientId = Object.keys(clientShows.value).find(id => 
      clientShows.value[id].some(s => s.id === show.id)
    )
    if (clientId) {
      const showIndex = clientShows.value[clientId].findIndex(s => s.id === show.id)
      if (showIndex !== -1) {
        clientShows.value[clientId][showIndex].status = 'quote_requested'
      }
    }
    
    alert('Quote request submitted successfully!')
    
  } catch (err) {
    console.error('Error requesting quote:', err)
    alert(`Error: ${err.message}`)
  } finally {
    updatingShow.value = null
  }
}

const viewShowLeads = (show) => {
  // Navigate to the admin leads page with show filter
  // We'll pass the show ID as a query parameter
  router.push(`/admin/leads?show_id=${show.id}`)
}

const getPublicUrl = (clientSlug, showSlug) => {
  return `${window.location.origin}/booth/${clientSlug}/${showSlug}`
}

const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text)
    alert('URL copied to clipboard!')
  } catch (err) {
    console.error('Failed to copy to clipboard:', err)
    alert('Failed to copy URL')
  }
}

const getStatusClass = (status) => {
  switch (status) {
    case 'active': return 'bg-emerald-50 text-emerald-700'
    case 'expired': return 'bg-red-50 text-red-700'
    case 'cancelled': return 'bg-gray-100 text-gray-700'
    default: return 'bg-gray-100 text-gray-700'
  }
}

const formatDate = (dateString) => fmtDate(dateString)

const navigateTo = (path) => {
  router.push(path)
}

// Subscription management methods
const viewSubscriptionDetails = async (clientId) => {
  try {
    loadingSubscription.value = true
    const token = localStorage.getItem('admin_token')
    
    const response = await fetch(`${config.apiBaseUrl}/admin/super-admin/clients/${clientId}/subscription`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })

    if (!response.ok) {
      throw new Error('Failed to load subscription details')
    }

    const data = await response.json()
    subscriptionDetails.value = data.subscription
    selectedClient.value = clients.value.find(c => c.id === clientId)
    showSubscriptionModal.value = true
    
  } catch (err) {
    console.error('Error loading subscription details:', err)
    alert(`Error: ${err.message}`)
  } finally {
    loadingSubscription.value = false
  }
}

const extendSubscription = (clientId) => {
  selectedClient.value = clients.value.find(c => c.id === clientId)
  extendDays.value = 30
  showExtendModal.value = true
}

const confirmExtendSubscription = async () => {
  try {
    loadingExtend.value = true
    const token = localStorage.getItem('admin_token')
    
    const response = await fetch(`${config.apiBaseUrl}/admin/super-admin/clients/${selectedClient.value.id}/extend-subscription`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ days: extendDays.value })
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.error || 'Failed to extend subscription')
    }

    const data = await response.json()
    
    // Update the client locally
    const clientIndex = clients.value.findIndex(c => c.id === selectedClient.value.id)
    if (clientIndex !== -1) {
      clients.value[clientIndex] = { ...clients.value[clientIndex], ...data.subscription }
    }
    
    alert(data.message)
    showExtendModal.value = false
    selectedClient.value = null
    
  } catch (err) {
    console.error('Error extending subscription:', err)
    alert(`Error: ${err.message}`)
  } finally {
    loadingExtend.value = false
  }
}

// Helper to check if a client can edit a show
const canClientEditShow = (show) => {
  // Clients can only edit shows before approval
  // Super admins can see these to help clients refine their requests
  return show.status === 'draft' || show.status === 'quote_requested'
}

// Lifecycle
onMounted(() => {
  loadClients()
})
</script> 